import * as React from 'react'

import { DraftLead } from '@user/store/draftLeads/typings'
import { DraftLeadHeader } from './DraftLeadHeader'
import { DraftLeadInformationList } from './InformationList'
import { DraftLeadLocationTag } from './LocationTag'
import { EntityCardBody } from '@user/components/EntityCards/Body'
import { EntityCardHeader } from '@user/components/EntityCards/Header'
import { EntityCardWrapper } from '@user/components/EntityCards/Wrapper'
import { EntityName } from '@user/components/EntityCards/atoms/EntityName'
import Box from '@mui/material/Box'
import AddressMapLink from '@user/features/MapView/AddressMapLink/AddressMapLink'
import { useMapViewFeatureEnabled } from '@user/store/tenantFeatures/hooks/useMapViewFeatureEnabled'
import { useData } from '@user/components/DraftLeads/Card/hooks/useData'
import { ENTITY_TYPES } from '@sales-center/common'
import Stack from '@mui/material/Stack'
import {
  getKnockStatusFromCard,
  MostRecentKnockStatus,
} from '@user/features/MapView/MostRecentKnockStatus'
import {
  PendingLeadAutoReviewBanner,
  UpdateRequiredFieldsDialog,
} from '@user/features/PendingLeadAutoReview'

interface Props {
  lead: DraftLead
  canDelete: boolean
}

export function DraftLeadCard(props: Props) {
  const {
    lead,
    lead: {
      id,
      branch,
      emailAddress,
      locationId,
      phoneNumber,
      locationCode,
      type,
      webFormCallTrackingEventId,
      submittedBy,
      contactName,
      createdTime,
      latitude,
      longitude,
    },
    canDelete,
  } = props
  const { formattedAddress } = useData({ lead })
  const mapViewEnabled = useMapViewFeatureEnabled()
  const [autoReviewOpen, setAutoReviewOpen] = React.useState(false)
  const openAutoReview = React.useCallback(() => setAutoReviewOpen(true), [])
  const closeAutoReview = React.useCallback(() => setAutoReviewOpen(false), [])

  return (
    <>
      <PendingLeadAutoReviewBanner lead={lead} onOpen={openAutoReview} />
      <MostRecentKnockStatus
        variant="card"
        knockStatus={getKnockStatusFromCard(
          lead.knockStatus,
          lead.knockStatusReason,
        )}
      />
      <EntityCardWrapper>
        <EntityCardHeader>
          <DraftLeadHeader lead={lead} canDelete={canDelete} />
        </EntityCardHeader>

        <EntityCardBody>
          <Box display="flex" flexDirection="column">
            <Box>
              <EntityName
                label="Pending Lead"
                name={`${contactName}`}
                entityId={id}
              />
            </Box>
            <Stack mb={'24px'} gap={'6px'}>
              {mapViewEnabled && (
                <AddressMapLink
                  address={formattedAddress}
                  showNoAddress={true}
                  entityId={id}
                  entityType={ENTITY_TYPES.DraftLead}
                  latitude={latitude}
                  longitude={longitude}
                >
                  {formattedAddress}
                </AddressMapLink>
              )}

              <DraftLeadLocationTag
                locationId={locationId}
                locationCode={locationCode}
              />
            </Stack>
          </Box>

          <DraftLeadInformationList
            branch={branch}
            email={emailAddress}
            phone={phoneNumber}
            submittedBy={submittedBy}
            type={type}
            webFormCallTrackingEventId={webFormCallTrackingEventId}
            draftLeadId={id}
            createdTime={createdTime}
          />
        </EntityCardBody>
      </EntityCardWrapper>
      <UpdateRequiredFieldsDialog
        lead={lead}
        open={autoReviewOpen}
        onClose={closeAutoReview}
      />
    </>
  )
}
